import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import Box from '@mui/material/Box'
import Modal from '@/v2/components/common/Modal'
import Switch from '@/v2/components/common/Switch'
import TextField from '@/v2/components/common/TextField'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import {
  useSettingsAppointmentTypeBookingWindows,
  useUpdateAppointmentTypeBookingWindows,
} from '@/v2/hooks/settings/useSettingsAppointmentTypeBookingWindows'
import { useSettingsClinicWeekHours } from '@/v2/hooks/settings/useSettingsClinicWeekHours'
import {
  ActionButton,
  DayRow,
  DayName,
  DayToggleCell,
  DayContent,
  DayHoursLine,
  TimeRangeGroup,
  DayNote,
  TimeSeparator,
} from '@/v2/components/settings/shared'

// Monday first because that is how a week reads here and in the clinic hours
// editor. The API keys its week from sunday, which only matters when a payload
// is built, never when one is shown.
const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

const DEFAULT_RANGE = { startAt: '09:00', stopsAt: '17:00' }
const ERROR_COLOR = '#dc2626'

const DayErrors = styled.div`
  font-size: 0.75rem;
  color: ${ERROR_COLOR};
  margin-top: 0.25rem;
`

// Fixed width so "+ Add hours" keeps its column on the first line of a day,
// where the extra ranges below have a remove button of their own.
const RemoveCell = styled.div`
  width: 88px;
  flex-shrink: 0;
`

const AddRangeButton = styled.button`
  padding: 0;
  font-size: 0.75rem;
  font-weight: 500;
  border: none;
  background: none;
  color: #2563eb;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    text-decoration: underline;
  }
`

const switchSx = { ml: 0, mr: 0 }

const UnrestrictedNote = styled.div`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  background-color: #fef3c7;
  color: #92400e;
  font-size: 0.8rem;
  line-height: 1.5;
`

const DiscardMessage = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.5;
`

const IntroNote = styled.p`
  margin: 0 0 1rem;
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.5;
`

// The clinic hours come back as full timestamps on a dummy date
// ("2000-01-01T10:00:00.000-05:00") whose wall clock is already the clinic's,
// so the HH:MM is read straight off the string rather than parsed into a Date,
// which would shift it into the browser's time zone.
const toClock = value => String(value || '').match(/(\d{2}:\d{2})/)?.[1] || ''

// The week the clinic is open, in the shape of this editor: one range for a day
// without a break, and two either side of it for a day with one.
const buildClinicWeek = weekHours =>
  DAYS.reduce((week, day) => {
    const hours = weekHours[day] || {}
    const opensAt = toClock(hours.opensAt)
    const closesAt = toClock(hours.closesAt)
    const breakFrom = toClock(hours.breakFrom)
    const breakTo = toClock(hours.breakTo)

    if (!hours.isOpen || !opensAt || !closesAt) {
      week[day] = []
    } else if (hours.hasBreak && breakFrom && breakTo) {
      week[day] = [
        { startAt: opensAt, stopsAt: breakFrom },
        { startAt: breakTo, stopsAt: closesAt },
      ]
    } else {
      week[day] = [{ startAt: opensAt, stopsAt: closesAt }]
    }
    return week
  }, {})

const toMinutes = value => {
  const [hours, minutes] = (value || '').split(':')
  return Number(hours) * 60 + Number(minutes)
}

const buildLocalState = data =>
  DAYS.reduce((week, day) => {
    week[day] = (data[day] || []).map(({ startAt, stopsAt }) => ({
      startAt: startAt || '',
      stopsAt: stopsAt || '',
    }))
    return week
  }, {})

// Every day is sent, including the empty ones: a PUT replaces the whole week, so
// a day left out would be wiped just the same. Sending it makes that explicit.
const buildPayload = week =>
  DAYS.reduce((payload, day) => {
    payload[day] = week[day].map(({ startAt, stopsAt }) => ({
      startAt,
      stopsAt,
    }))
    return payload
  }, {})

// Half-open, matching the server: a range stopping at 12:00 and one starting at
// 12:00 sit next to each other rather than overlapping.
const overlaps = (a, b) =>
  toMinutes(a.startAt) < toMinutes(b.stopsAt) &&
  toMinutes(b.startAt) < toMinutes(a.stopsAt)

const validateDay = ranges => {
  const errors = ranges.map(({ startAt, stopsAt }) => {
    if (!startAt || !stopsAt) return 'Both times are required'
    if (toMinutes(stopsAt) <= toMinutes(startAt)) {
      return 'The end time must be after the start time'
    }
    return null
  })

  ranges.forEach((range, index) => {
    if (errors[index]) return

    const clashes = ranges.some(
      (other, otherIndex) =>
        otherIndex !== index && !errors[otherIndex] && overlaps(range, other)
    )
    if (clashes) errors[index] = 'Overlaps another range on this day'
  })

  return errors
}

const validateWeek = week =>
  DAYS.reduce((errors, day) => {
    const dayErrors = validateDay(week[day])
    if (dayErrors.some(Boolean)) errors[day] = dayErrors
    return errors
  }, {})

// The server writes for a Rails audience and in a 12-hour clock the editor never
// shows, so its wording is replaced wherever it is recognised. Anything new falls
// through untouched rather than being swallowed.
const humanizeServerError = message => {
  if (/overlaps another booking window/i.test(message)) {
    return 'Overlaps another range on this day'
  }
  if (/must be after/i.test(message)) {
    return 'The end time must be after the start time'
  }
  if (/can't be blank/i.test(message)) return 'Both times are required'
  if (/not a valid time/i.test(message)) return 'Enter a valid time'
  return message
}

// A 422 lists one entry per range sent for a day, in the order it was sent, and
// leaves out the days that were fine. Its field keys stay snake_case: only the
// success payload is camelized on the way out.
const serverErrorsToWeek = (errorsByDay, week) =>
  Object.entries(errorsByDay).reduce((errors, [day, rangeErrors]) => {
    if (!DAYS.includes(day) || !Array.isArray(rangeErrors)) return errors

    const messages = week[day].map((_range, index) => {
      const fields = rangeErrors[index]
      if (!fields) return null

      const flattened = Object.values(fields).flat()
      return flattened.length ? humanizeServerError(flattened[0]) : null
    })

    if (messages.some(Boolean)) errors[day] = messages
    return errors
  }, {})

const AppointmentTypeBookingRules = ({ appointmentType, onClose }) => {
  const { id, title } = appointmentType
  const {
    bookingWindows,
    isLoading,
  } = useSettingsAppointmentTypeBookingWindows(id)
  const {
    updateBookingWindows,
    isLoading: isSaving,
  } = useUpdateAppointmentTypeBookingWindows()
  const { onOpenSnackbar } = useSnackbarContext()

  const {
    weekHours,
    isLoading: isLoadingClinicHours,
  } = useSettingsClinicWeekHours()

  const [week, setWeek] = useState(null)
  const [errors, setErrors] = useState({})
  // Only read when a day is switched on, never to fill the week on opening:
  // a type starts from its saved rules, or from nothing.
  const clinicWeek = useMemo(
    () => (isLoadingClinicHours ? null : buildClinicWeek(weekHours)),
    [isLoadingClinicHours, weekHours]
  )

  useEffect(() => {
    if (!isLoading) setWeek(buildLocalState(bookingWindows))
  }, [bookingWindows, isLoading])

  const updateDay = (day, ranges) => {
    setWeek(prev => ({ ...prev, [day]: ranges }))
    setErrors(prev => {
      if (!prev[day]) return prev
      const { [day]: _cleared, ...rest } = prev
      return rest
    })
  }

  // A day switched on opens with the clinic's own hours for it when there are
  // any, so the common case needs no typing.
  const handleToggleDay = (day, checked) => {
    if (!checked) return updateDay(day, [])

    const clinicRanges = clinicWeek?.[day] || []
    updateDay(
      day,
      clinicRanges.length
        ? clinicRanges.map(range => ({ ...range }))
        : [{ ...DEFAULT_RANGE }]
    )
  }

  // A new range picks up where the previous one stopped, so adding one never
  // lands on an overlap the user then has to clear.
  const handleAddRange = day => {
    const ranges = week[day]
    const previous = ranges[ranges.length - 1]
    updateDay(day, [
      ...ranges,
      previous?.stopsAt
        ? { startAt: previous.stopsAt, stopsAt: '' }
        : { ...DEFAULT_RANGE },
    ])
  }

  const handleRemoveRange = (day, index) =>
    updateDay(
      day,
      week[day].filter((_range, rangeIndex) => rangeIndex !== index)
    )

  const handleRangeChange = (day, index, field, value) =>
    updateDay(
      day,
      week[day].map((range, rangeIndex) =>
        rangeIndex === index ? { ...range, [field]: value } : range
      )
    )

  const handleSave = async () => {
    if (!week) return

    const validationErrors = validateWeek(week)
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors)
      return
    }

    try {
      await updateBookingWindows({
        appointmentTypeId: id,
        bookingWindows: buildPayload(week),
      })
      onOpenSnackbar('Booking rules updated successfully', 'success')
      onClose()
    } catch (error) {
      const errorsByDay = error.errorsByDay

      if (errorsByDay) {
        setErrors(serverErrorsToWeek(errorsByDay, week))
        onOpenSnackbar('Please fix the highlighted booking rules', 'error')
        return
      }

      onOpenSnackbar(error.message || 'Failed to update booking rules', 'error')
    }
  }

  const isUnrestricted = week && DAYS.every(day => week[day].length === 0)

  // Compared through the payload so only what would be sent counts, and not the
  // ids the saved windows carry. Opening a day and closing it again is no change.
  const savedPayload = useMemo(
    () =>
      isLoading
        ? null
        : JSON.stringify(buildPayload(buildLocalState(bookingWindows))),
    [bookingWindows, isLoading]
  )
  const hasUnsavedChanges =
    !!week &&
    !!savedPayload &&
    JSON.stringify(buildPayload(week)) !== savedPayload

  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false)

  // Every way out of the modal — Cancel, the close button, the backdrop and
  // Escape — lands here. A successful save closes through onClose directly.
  const handleRequestClose = () => {
    if (hasUnsavedChanges) setIsConfirmingDiscard(true)
    else onClose()
  }

  return (
    <>
      <Modal
        isOpen
        title={`Reschedule link hours — ${title}`}
        onClose={handleRequestClose}
        onConfirm={handleSave}
        confirmLabel={isSaving ? 'Saving...' : 'Save rules'}
        cancelLabel="Cancel"
        submitDisabled={isSaving}
        loading={isLoading || !week}
        maxWidth="md"
        fullWidth
      >
        {week && (
          <Box>
            <IntroNote>
              Choose the days and hours a reschedule link can offer this
              appointment type. These hours don't change your booking page. A
              day can hold more than one stretch of hours, for a morning and an
              afternoon either side of lunch. An end time is exclusive, so
              8:00–12:00 and 12:00–17:00 sit next to each other.
            </IntroNote>

            {DAYS.map(day => {
              const ranges = week[day]
              const dayErrors = errors[day] || []

              return (
                <DayRow key={day} $alignTop="flex-start">
                  <DayName $closed={ranges.length === 0}>{day}</DayName>

                  <DayToggleCell>
                    <Switch
                      label={ranges.length > 0 ? 'Open' : 'Closed'}
                      checked={ranges.length > 0}
                      onChange={(e, checked) => handleToggleDay(day, checked)}
                      sx={switchSx}
                    />
                  </DayToggleCell>

                  <DayContent>
                    {ranges.length === 0 ? (
                      <DayNote>Not offered this day</DayNote>
                    ) : (
                      ranges.map((range, index) => (
                        <DayHoursLine key={index}>
                          <TimeRangeGroup>
                            <TextField
                              type="time"
                              value={range.startAt}
                              onChange={value =>
                                handleRangeChange(day, index, 'startAt', value)
                              }
                              outlineColor={
                                dayErrors[index] ? ERROR_COLOR : undefined
                              }
                              size="small"
                            />
                            <TimeSeparator>to</TimeSeparator>
                            <TextField
                              type="time"
                              value={range.stopsAt}
                              onChange={value =>
                                handleRangeChange(day, index, 'stopsAt', value)
                              }
                              outlineColor={
                                dayErrors[index] ? ERROR_COLOR : undefined
                              }
                              size="small"
                            />
                          </TimeRangeGroup>

                          <RemoveCell>
                            <ActionButton
                              type="button"
                              $variant="danger"
                              aria-label={`Remove hours from ${day}`}
                              onClick={() => handleRemoveRange(day, index)}
                            >
                              Remove
                            </ActionButton>
                          </RemoveCell>

                          {index === 0 && (
                            <AddRangeButton
                              type="button"
                              onClick={() => handleAddRange(day)}
                            >
                              + Add hours
                            </AddRangeButton>
                          )}
                        </DayHoursLine>
                      ))
                    )}

                    {dayErrors.filter(Boolean).length > 0 && (
                      <DayErrors>
                        {[...new Set(dayErrors.filter(Boolean))].map(
                          message => (
                            <div key={message}>{message}</div>
                          )
                        )}
                      </DayErrors>
                    )}
                  </DayContent>
                </DayRow>
              )
            })}

            {isUnrestricted && (
              <UnrestrictedNote>
                No hours set. Reschedule links can't be sent for this
                appointment type until you open at least one day.
              </UnrestrictedNote>
            )}
          </Box>
        )}
      </Modal>

      <Modal
        isOpen={isConfirmingDiscard}
        title="Discard unsaved changes?"
        onClose={() => setIsConfirmingDiscard(false)}
        onConfirm={onClose}
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        maxWidth="xs"
        fullWidth
      >
        <DiscardMessage>
          Your changes to the hours for {title} haven't been saved. If you leave
          now, they will be lost.
        </DiscardMessage>
      </Modal>
    </>
  )
}

export default AppointmentTypeBookingRules
